import './CoralHeading.scss'

import { MapCTAProps } from '@websrc/DataMappers/mapCTA'
import React, { FC, useEffect, useRef, useState } from 'react'

import { CTA } from '../CTA'
import { Markdown } from '../Markdown/Markdown'

interface CoralHeadingProps {
  header: string | null | undefined
  description?: string | null | undefined
  cta?: MapCTAProps | null | undefined
  animate?: boolean
  headerTag?: string
}

export const CoralHeading: FC<CoralHeadingProps> = ({
  header,
  description,
  cta,
  animate = true,
  headerTag = 'h1',
}) => {
  const [visible, setVisible] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!animate) return
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 }
    )
    if (ref.current) observer.observe(ref.current)
    return () => observer.disconnect()
  }, [animate])

  const headerWords = header ? header.split(' ') : []

  return (
    <div className={'coral-heading__container'} ref={ref}>
      <h2 className={`coral-heading__header m--tag-${headerTag}`}>
        {animate ? (
          headerWords.map((word, wIdx) => (
            <span className={'coral-heading__word'} key={wIdx}>
              {word.split('').map((letter, lIdx) => (
                <span
                  aria-hidden={true}
                  className={`coral-heading__letter${
                    visible ? ' coral-heading__letter-animate' : ''
                  }`}
                  key={lIdx}
                  style={{ transitionDelay: `${(wIdx * 10 + lIdx) * 0.01}s` }}
                >
                  {letter}
                </span>
              ))}
              {wIdx !== headerWords.length - 1 && ' '}
            </span>
          ))
        ) : (
          <>{header}</>
        )}
      </h2>
      {(description || cta) && (
        <div className={'coral-heading__content'}>
          {description && (
            <div>
              {animate ? (
                <Markdown
                  className={`coral-heading__description${
                    visible ? ' coral-heading__description-animate' : ''
                  }`}
                  markdown={description}
                />
              ) : (
                <Markdown
                  className={`coral-heading__description-non-animate`}
                  markdown={description}
                />
              )}
            </div>
          )}
          {cta && (
            <CTA
              className={'coral-global-button m--type-fill m--theme-white m--width-auto'}
              cta={cta}
              iconName={'upRightArrow'}
              type={'clean'}
            />
          )}
        </div>
      )}
    </div>
  )
}
